/**
 * WeedEditorDrawer — entity-specific drawer для «Сорняки» (AK-234).
 *
 * Секции:
 *   1. Идентификация (название RU + латынь + род латынь + EPPO)
 *   2. Таксономия (категория + жизненный цикл — select'ы из мини-справочников
 *      reference.weed_categories / reference.weed_life_cycles, migration 106)
 *   3. Характеристики (семейство латынь, флаги паразит/карантин/инвазив, ЭПВ)
 *
 * Списки категорий и жизненных циклов приходят из API (platform-admin может
 * их переименовать/добавить), захардкоженных enum'ов больше нет. Легаси-текст
 * `category` / `life_cycle` (NOT NULL) держим синхронным с `code` выбранной
 * строки — бэкенд дублирует эту синхронизацию. EN-перевод отложен (RU-таб).
 */
import { useEffect, useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  EditorSection,
  ReferenceCatalogDrawer,
} from '@/components/reference/ReferenceCatalogDrawer';
import { NameTranslationsField } from '@/components/reference/NameTranslationsField';
import { StringTagsField } from '@/components/reference/StringTagsField';
import { AffectedCropsField } from '@/components/reference/AffectedCropsField';
import { PhotoBank } from '@/components/reference/PhotoBank';
import { describeApiError } from '@/lib/apiError';
import { toast } from '@/lib/toast';
import { useWeed } from '@/pages/platform-admin/reference/hooks/useCatalogQueries';
import {
  type AffectedCrop,
  type NameTranslations,
  type ReferenceCategory,
  type WeedDetail,
  type WeedListItem,
  type WeedPayload,
} from '@/pages/platform-admin/reference/types';

interface FormState {
  name_translations: NameTranslations;
  latin_name: string;
  genus_latin: string;
  eppo_code: string;
  category_id: string;
  life_cycle_id: string;
  family_latin: string;
  is_parasitic: boolean;
  is_quarantine: boolean;
  is_invasive: boolean;
  economic_threshold_value: string;
  economic_threshold_unit: string;
  also_known_as: string[];
}

const EMPTY_FORM: FormState = {
  name_translations: { ru: '' },
  latin_name: '',
  genus_latin: '',
  eppo_code: '',
  category_id: '',
  life_cycle_id: '',
  family_latin: '',
  is_parasitic: false,
  is_quarantine: false,
  is_invasive: false,
  economic_threshold_value: '',
  economic_threshold_unit: '',
  also_known_as: [],
};

/**
 * Build form from the FULL detail row (PR #290 B1). Previously took a
 * WeedListItem and hardcoded genus_latin / family_latin / parasitic / quarantine
 * / invasive / ЭПВ to empty, which the full-replace PATCH then wiped — silent
 * data loss.
 */
function weedToForm(w: WeedDetail): FormState {
  return {
    name_translations: { ...w.name_translations },
    latin_name: w.latin_name ?? '',
    genus_latin: w.genus_latin ?? '',
    eppo_code: w.eppo_code ?? '',
    category_id: w.category_id ?? '',
    life_cycle_id: w.life_cycle_id ?? '',
    family_latin: w.family_latin ?? '',
    is_parasitic: w.is_parasitic ?? false,
    is_quarantine: w.is_quarantine ?? false,
    is_invasive: w.is_invasive ?? false,
    economic_threshold_value:
      w.economic_threshold_value == null
        ? ''
        : String(w.economic_threshold_value),
    economic_threshold_unit: w.economic_threshold_unit ?? '',
    also_known_as: [...(w.also_known_as ?? [])],
  };
}

interface FormErrors {
  name?: string;
  category?: string;
  life_cycle?: string;
}

function validate(f: FormState): FormErrors {
  const errs: FormErrors = {};
  const hasName = Object.values(f.name_translations).some((v) => v.trim().length > 0);
  if (!hasName) errs.name = 'Требуется название';
  if (!f.category_id) errs.category = 'Выберите категорию';
  if (!f.life_cycle_id) errs.life_cycle = 'Выберите жизненный цикл';
  return errs;
}

const numOrNull = (v: string): number | null => (v.trim() ? Number(v) : null);

function toPayload(
  f: FormState,
  categories: ReferenceCategory[],
  lifeCycles: ReferenceCategory[],
): WeedPayload {
  const trimmed: NameTranslations = {};
  for (const [k, v] of Object.entries(f.name_translations)) {
    if (v.trim()) trimmed[k] = v.trim();
  }
  const cat = categories.find((c) => c.id === f.category_id);
  const lc = lifeCycles.find((c) => c.id === f.life_cycle_id);
  return {
    name_translations: trimmed,
    latin_name: f.latin_name.trim() || null,
    // Легаси-текст (NOT NULL) держим равным коду выбранной строки справочника.
    category: cat?.code ?? '',
    category_id: f.category_id || null,
    life_cycle: lc?.code ?? '',
    life_cycle_id: f.life_cycle_id || null,
    family_latin: f.family_latin.trim() || null,
    is_parasitic: f.is_parasitic,
    is_quarantine: f.is_quarantine,
    is_invasive: f.is_invasive,
    economic_threshold_value: numOrNull(f.economic_threshold_value),
    economic_threshold_unit: f.economic_threshold_unit.trim() || null,
    genus_latin: f.genus_latin.trim() || null,
    also_known_as: f.also_known_as,
  };
}

export interface WeedEditorDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  weed: WeedListItem | null;
  /** Мини-справочник `reference.weed_categories` (загружается страницей). */
  categories: ReferenceCategory[];
  /** Мини-справочник `reference.weed_life_cycles`. */
  lifeCycles: ReferenceCategory[];
  onSubmit: (payload: WeedPayload) => Promise<void>;
}

export function WeedEditorDrawer({
  open,
  onOpenChange,
  mode,
  weed,
  categories,
  lifeCycles,
  onSubmit,
}: WeedEditorDrawerProps) {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  // AK-257 — affected crops feed the PhotoBank lenses.
  const [affectedCrops, setAffectedCrops] = useState<AffectedCrop[]>([]);

  // PR #290 B1 — load full detail in edit mode (see useWeed / weedToForm).
  const editId = open && mode === 'edit' && weed ? weed.id : null;
  const detailQ = useWeed(editId);
  const detailLoading = Boolean(editId) && detailQ.isLoading;

  useEffect(() => {
    if (!open) return;
    if (mode === 'create') {
      setForm(EMPTY_FORM);
      setTouched(false);
      return;
    }
    if (detailQ.data) {
      setForm(weedToForm(detailQ.data));
      setTouched(false);
    }
  }, [open, mode, detailQ.data]);

  const errors = validate(form);
  const hasErrors = Object.keys(errors).length > 0;

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const handleSave = async () => {
    setTouched(true);
    if (hasErrors) return;
    setSaving(true);
    try {
      await onSubmit(toPayload(form, categories, lifeCycles));
      onOpenChange(false);
    } catch (err) {
      // AK-574 — отказ сервера виден пользователю: drawer остаётся открытым,
      // текст 409/422 в тосте. Раньше промах уходил в unhandled rejection.
      toast.error(describeApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const show = (err?: string) => (touched && err ? err : undefined);

  return (
    <ReferenceCatalogDrawer
      open={open}
      onOpenChange={onOpenChange}
      mode={mode}
      entityLabel="сорняка"
      headerSlot={
        form.name_translations.ru ? (
          <span className="text-sm font-medium text-foreground">
            {form.name_translations.ru}
          </span>
        ) : undefined
      }
      saveDisabled={detailLoading || (touched && hasErrors)}
      saveLoading={saving}
      onSave={handleSave}
    >
      {detailLoading ? (
        <div className="space-y-4 p-1">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : (
        <>
      <EditorSection number={1} title="Идентификация">
        <div className="mb-4 grid grid-cols-2 gap-4">
          <div>
            <Label className="mb-1.5 block text-sm">Латинское название</Label>
            <Input
              value={form.latin_name}
              onChange={(e) => set('latin_name', e.target.value)}
              placeholder="Amaranthus retroflexus"
              className="italic"
            />
          </div>
          <div>
            <Label className="mb-1.5 block text-sm">EPPO-код</Label>
            <Input
              value={form.eppo_code}
              onChange={(e) => set('eppo_code', e.target.value)}
              placeholder="AMARE"
              className="font-mono uppercase"
            />
          </div>
        </div>
        <Label className="mb-1.5 block text-sm">
          Название (RU) <span className="text-destructive">*</span>
        </Label>
        <NameTranslationsField
          value={form.name_translations}
          onChange={(v) => set('name_translations', v)}
          locales={['ru']}
          hint="EN-перевод отложен (отдельный тикет). Сейчас ведём только RU."
          hasError={Boolean(show(errors.name))}
        />
        <Label className="mb-1.5 mt-4 block text-sm">
          Другие названия (народные/местные)
        </Label>
        <StringTagsField
          values={form.also_known_as}
          onChange={(v) => set('also_known_as', v)}
          placeholder="напр. «щирица» (Enter добавит)"
          removeLabel="Убрать синоним"
        />
        <p className="mt-1.5 text-xs text-muted-foreground">
          Народные и местные синонимы. По ним ищут в каталоге и подбирают в
          реестре СЗР наравне с основным именем.
        </p>
      </EditorSection>

      <EditorSection
        number={2}
        title="Таксономия"
        hint="из мини-справочников сорняков"
      >
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="mb-1.5 block text-sm">
              Категория <span className="text-destructive">*</span>
            </Label>
            <Select
              value={form.category_id || undefined}
              onValueChange={(v) => set('category_id', v)}
            >
              <SelectTrigger
                className={show(errors.category) ? 'border-destructive' : undefined}
              >
                <SelectValue placeholder="Категория…" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name_translations.ru ?? c.code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {show(errors.category) && (
              <p className="mt-1.5 flex items-center gap-1 text-xs text-destructive">
                <AlertCircle className="h-3.5 w-3.5" />
                {errors.category}
              </p>
            )}
          </div>
          <div>
            <Label className="mb-1.5 block text-sm">
              Жизненный цикл <span className="text-destructive">*</span>
            </Label>
            <Select
              value={form.life_cycle_id || undefined}
              onValueChange={(v) => set('life_cycle_id', v)}
            >
              <SelectTrigger
                className={show(errors.life_cycle) ? 'border-destructive' : undefined}
              >
                <SelectValue placeholder="Цикл…" />
              </SelectTrigger>
              <SelectContent>
                {lifeCycles.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name_translations.ru ?? c.code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {show(errors.life_cycle) && (
              <p className="mt-1.5 flex items-center gap-1 text-xs text-destructive">
                <AlertCircle className="h-3.5 w-3.5" />
                {errors.life_cycle}
              </p>
            )}
          </div>
        </div>
      </EditorSection>

      <EditorSection number={3} title="Характеристики">
        <div className="mb-4 grid grid-cols-2 gap-4">
          <div>
            <Label className="mb-1.5 block text-sm">Род (латынь)</Label>
            <Input
              value={form.genus_latin}
              onChange={(e) => set('genus_latin', e.target.value)}
              placeholder="Amaranthus"
              className="italic"
            />
          </div>
          <div>
            <Label className="mb-1.5 block text-sm">Семейство (латынь)</Label>
            <Input
              value={form.family_latin}
              onChange={(e) => set('family_latin', e.target.value)}
              placeholder="Amaranthaceae"
              className="italic"
            />
          </div>
        </div>

        <div className="mb-4 flex flex-wrap gap-6">
          <label className="flex items-center gap-2 text-sm">
            <Switch
              checked={form.is_parasitic}
              onCheckedChange={(v) => set('is_parasitic', v)}
            />
            Паразитический
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Switch
              checked={form.is_quarantine}
              onCheckedChange={(v) => set('is_quarantine', v)}
            />
            Карантинный
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Switch
              checked={form.is_invasive}
              onCheckedChange={(v) => set('is_invasive', v)}
            />
            Инвазивный
          </label>
        </div>

        <Label className="mb-1.5 block text-sm">
          Экономический порог вредоносности (ЭПВ)
        </Label>
        <div className="grid grid-cols-2 gap-4">
          <Input
            type="number"
            value={form.economic_threshold_value}
            onChange={(e) => set('economic_threshold_value', e.target.value)}
            placeholder="значение"
          />
          <Input
            value={form.economic_threshold_unit}
            onChange={(e) => set('economic_threshold_unit', e.target.value)}
            placeholder="шт./м²"
          />
        </div>
      </EditorSection>

      {/* AK-257 — section 4: affected crops (write-path, Q2 two-step). */}
      <EditorSection
        number={4}
        title="Поражаемые культуры"
        hint="кого засоряет сорняк"
      >
        <AffectedCropsField
          dict="weeds"
          entityId={editId}
          onCropsChange={setAffectedCrops}
        />
      </EditorSection>

      {/* AK-257 — section 5: photobank. Saved entity + ≥1 affected crop only. */}
      {editId && affectedCrops.length > 0 && (
        <EditorSection number={5} title="Фотобанк" hint="фото по культурам">
          <PhotoBank dict="weeds" entityId={editId} affectedCrops={affectedCrops} />
        </EditorSection>
      )}
        </>
      )}
    </ReferenceCatalogDrawer>
  );
}

export default WeedEditorDrawer;
